import { SiteInfo } from '../views/CalendarMultiView'
import { GanttTask } from '../types/gantt.types'
import { useInvalidateSignals } from './useTaskSignals'
import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { addDays, formatISO } from '../constants/gantt.logic'
import { patchShipOrderSchedule } from '../api/shipApi'
// 발주·입고 마커 드래그 — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttMarkerDragArgs = {
  DWRef: React.MutableRefObject<number>
  selectedSite: SiteInfo | undefined
  tasksRef: React.MutableRefObject<GanttTask[]>
  setTasks: React.Dispatch<React.SetStateAction<GanttTask[]>>
  invalidateSignals: ReturnType<typeof useInvalidateSignals>
}

export function useGanttMarkerDrag({ DWRef, selectedSite, tasksRef, setTasks, invalidateSignals }: UseGanttMarkerDragArgs) {
  const [draggingMkId,     setDraggingMkId]     = useState<{ taskId: number; type: 'order' | 'delivery' } | null>(null)
  const [draggingMkDelta,  setDraggingMkDelta]  = useState(0)
  const mkDragCtxRef   = useRef<{ taskId: number; type: 'order' | 'delivery'; startX: number } | null>(null)

  // ── 발주/입고 마커 드래그 ────────────────────────────────────────
  const onMkMouseDown = (e: React.MouseEvent, taskId: number, type: 'order' | 'delivery') => {
    e.stopPropagation()
    e.preventDefault()
    mkDragCtxRef.current = { taskId, type, startX: e.clientX }
    setDraggingMkId({ taskId, type })
    setDraggingMkDelta(0)
  }

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!mkDragCtxRef.current) return
      const delta = Math.round((e.clientX - mkDragCtxRef.current.startX) / DWRef.current)
      setDraggingMkDelta(delta)
    }
    const onUp = async (e: MouseEvent) => {
      const ctx = mkDragCtxRef.current
      if (!ctx) return
      const delta = Math.round((e.clientX - ctx.startX) / DWRef.current)
      mkDragCtxRef.current = null
      setDraggingMkId(null)
      setDraggingMkDelta(0)
      if (delta === 0) return
      const sid = selectedSite?.scheduleId
      if (!sid) return
      const task = tasksRef.current.find(t => t.taskId === ctx.taskId)
      if (!task) return
      const currentDate = ctx.type === 'order' ? task.orderDate : task.deliveryDate
      if (!currentDate) return
      const newDate = addDays(new Date(currentDate), delta)
      // 공정 종료일 이후 이동은 막지 않는다(5단계 M4) — 서버가 경고만 냄(경고 표시는 WP11 이동 확인 창에서 다룸, 6단계 §9.3)
      const newDateStr = formatISO(newDate)
      setTasks(prev => prev.map(t =>
        t.taskId === ctx.taskId
          ? { ...t, ...(ctx.type === 'order' ? { orderDate: newDateStr } : { deliveryDate: newDateStr }) }
          : t
      ))
      try {
        // 바뀐 필드 한 개만 C9 PATCH(전체 배열 PUT 은 조회 실패 시 덮어쓰기 위험 — D6)
        const saved = await patchShipOrderSchedule(
          ctx.taskId,
          ctx.type === 'order' ? { orderDate: newDateStr } : { deliveryDate: newDateStr },
        )
        // 서버 응답의 날짜로 로컬을 맞춘다(응답에 필드가 없으면 입력값 유지)
        const sv = saved as { orderDate?: string | null; deliveryDate?: string | null } | null | undefined
        if (sv) {
          setTasks(prev => prev.map(t =>
            t.taskId === ctx.taskId
              ? { ...t, orderDate: sv.orderDate !== undefined ? sv.orderDate : t.orderDate, deliveryDate: sv.deliveryDate !== undefined ? sv.deliveryDate : t.deliveryDate }
              : t
          ))
        }
        void invalidateSignals()
      } catch {
        setTasks(prev => prev.map(t =>
          t.taskId === ctx.taskId
            ? { ...t, ...(ctx.type === 'order' ? { orderDate: task.orderDate } : { deliveryDate: task.deliveryDate }) }
            : t
        ))
        // 저장 실패를 말없이 되돌리지 않는다(E-36)
        toast.error(`${ctx.type === 'order' ? '발주일' : '입고일'}을 바꾸지 못했어요. 원래 날짜로 되돌렸어요. 잠시 후 다시 시도해 주세요`)
      }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [selectedSite?.scheduleId])

  return { draggingMkId, draggingMkDelta, onMkMouseDown }
}
